import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LayoutChangeEvent,
  Modal,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from 'react-native'
import Svg, { Circle, Line, Polyline } from 'react-native-svg'
import { colors } from '@/src/theme'
import { body4, headline2 } from '@/src/theme/typography'

import type {
  VoiceAssessmentChartXLabel,
  VoiceAssessmentScorePoint,
} from '../../utils/voice-assessment-score-series'
import {
  clampChartOverlayValue,
  formatVoiceAssessmentChartDate,
} from './voiceAssessmentChartOverlay'

const Y_MIN = 0
const Y_MAX = 100
/** Singing score line — Figma: 1px solid var(--Core-Pink). */
const CHART_LINE = colors.corePink
const GRID_COLOR = colors.border
const CHART_HEIGHT = 140
const Y_AXIS_WIDTH = 30
const PADDING_X = 8
const PADDING_Y = 8
const X_AXIS_LABEL_HEIGHT = 48
const POINT_HIT_SIZE = 44
const OVERLAY_WIDTH = 148
const OVERLAY_HEIGHT = 72
const OVERLAY_MARGIN = 12

function parseXLabel(label: VoiceAssessmentChartXLabel | undefined): {
  line1: string
  line2?: string
} {
  if (label == null) {
    return { line1: '' }
  }
  if (typeof label === 'string') {
    return { line1: label }
  }
  return { line1: label.line1, line2: label.line2 }
}

interface VoiceAssessmentStatisticsLineChartProps {
  points: VoiceAssessmentScorePoint[]
  xLabels: VoiceAssessmentChartXLabel[]
  visibleLabelCount: number
  columnWidthMultiplier: number
  locale: string
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function buildXLabelSlots({
  xLabels,
  plotContentWidth,
}: {
  xLabels: VoiceAssessmentChartXLabel[]
  plotContentWidth: number
}) {
  const parsedLabels = xLabels.map(parseXLabel)
  const visibleIndexes = parsedLabels.flatMap((label, index) =>
    label.line1.length > 0 || (label.line2?.length ?? 0) > 0 ? [index] : [],
  )
  const minVisibleSpacing = visibleIndexes.reduce<number | null>(
    (minSpacing, index, visibleIndex) => {
      const previous = visibleIndexes[visibleIndex - 1]
      if (previous == null) {
        return minSpacing
      }
      const spacing = index - previous
      return minSpacing == null ? spacing : Math.min(minSpacing, spacing)
    },
    null,
  )
  const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)
  const stepWidth = xLabels.length > 1 ? innerW / (xLabels.length - 1) : innerW
  const slotWidth = Math.min(
    plotContentWidth,
    Math.max(32, stepWidth * Math.max(1, minVisibleSpacing ?? 1)),
  )

  return visibleIndexes.map((index) => {
    const centerX =
      xLabels.length <= 1 ? plotContentWidth / 2 : PADDING_X + stepWidth * index
    return {
      ...parsedLabels[index],
      index,
      left: centerX - slotWidth / 2,
      width: slotWidth,
    }
  })
}

export function VoiceAssessmentStatisticsLineChart({
  points,
  xLabels,
  visibleLabelCount,
  columnWidthMultiplier,
  locale,
}: VoiceAssessmentStatisticsLineChartProps) {
  const scrollViewRef = useRef<ScrollView>(null)
  const chartViewportRef = useRef<View>(null)
  const scrollXRef = useRef(0)
  const [plotViewportWidth, setPlotViewportWidth] = useState(0)
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const [selectedPoint, setSelectedPoint] = useState<{
    point: VoiceAssessmentScorePoint
    anchorX: number
    anchorY: number
    viewportX: number
    viewportY: number
    scrollX: number
  } | null>(null)

  const onChartLayout = (event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.width
    if (next > 0) {
      setPlotViewportWidth(next)
    }
  }

  const plotContentWidth = useMemo(() => {
    if (plotViewportWidth <= 0) {
      return 0
    }

    const visibleColumnCount = Math.max(1, visibleLabelCount)
    const totalColumnCount = Math.max(1, xLabels.length - 1)
    const baseWidth =
      (plotViewportWidth / visibleColumnCount) * totalColumnCount
    return Math.max(
      plotViewportWidth,
      Math.floor(baseWidth * columnWidthMultiplier),
    )
  }, [
    columnWidthMultiplier,
    plotViewportWidth,
    visibleLabelCount,
    xLabels.length,
  ])

  const { polylineSegments, linePoints } = useMemo(() => {
    if (plotContentWidth <= 0 || points.length === 0) {
      return {
        polylineSegments: [] as string[],
        linePoints: [] as {
          x: number
          y: number
          segmentId: string
          point: VoiceAssessmentScorePoint
        }[],
      }
    }

    const span = Y_MAX - Y_MIN
    const plotH = CHART_HEIGHT - PADDING_Y * 2
    const innerW = Math.max(0, plotContentWidth - 2 * PADDING_X)

    const pts: {
      x: number
      y: number
      segmentId: string
      point: VoiceAssessmentScorePoint
    }[] = points.map((point) => {
      const score = clamp(point.score, Y_MIN, Y_MAX)
      const x = PADDING_X + clamp(point.x, 0, 1) * innerW
      const y = PADDING_Y + ((Y_MAX - score) / span) * plotH
      return { x, y, segmentId: point.segmentId, point }
    })

    const segments: string[] = []
    let currentSegmentId: string | null = null
    let currentSegment: { x: number; y: number }[] = []

    pts.forEach((point) => {
      if (currentSegmentId != null && point.segmentId !== currentSegmentId) {
        if (currentSegment.length > 1) {
          segments.push(
            currentSegment
              .map(
                (segmentPoint) =>
                  `${String(segmentPoint.x)},${String(segmentPoint.y)}`,
              )
              .join(' '),
          )
        }
        currentSegment = []
      }
      currentSegmentId = point.segmentId
      currentSegment.push(point)
    })

    if (currentSegment.length > 1) {
      segments.push(
        currentSegment
          .map((point) => `${String(point.x)},${String(point.y)}`)
          .join(' '),
      )
    }

    return { polylineSegments: segments, linePoints: pts }
  }, [plotContentWidth, points])

  const yTicks = [100, 75, 50, 25, 0]
  const xLabelSlots = useMemo(
    () =>
      plotContentWidth > 0
        ? buildXLabelSlots({ xLabels, plotContentWidth })
        : [],
    [plotContentWidth, xLabels],
  )

  useEffect(() => {
    if (plotContentWidth <= plotViewportWidth) {
      return
    }

    const frame = requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo({
        x: plotContentWidth - plotViewportWidth,
        animated: false,
      })
    })

    return () => {
      cancelAnimationFrame(frame)
    }
  }, [plotContentWidth, plotViewportWidth])

  const activeSelectedPoint =
    selectedPoint != null && points.includes(selectedPoint.point)
      ? selectedPoint
      : null
  const selectedLinePoint =
    activeSelectedPoint == null
      ? null
      : (linePoints.find(
          (point) => point.point === activeSelectedPoint.point,
        ) ?? null)
  const overlayLeft =
    activeSelectedPoint == null
      ? 0
      : clampChartOverlayValue(
          activeSelectedPoint.anchorX - OVERLAY_WIDTH / 2,
          OVERLAY_MARGIN,
          Math.max(
            OVERLAY_MARGIN,
            windowWidth - OVERLAY_WIDTH - OVERLAY_MARGIN,
          ),
        )
  const overlayTop =
    activeSelectedPoint == null
      ? 0
      : clampChartOverlayValue(
          activeSelectedPoint.anchorY - OVERLAY_HEIGHT - 10,
          OVERLAY_MARGIN,
          Math.max(
            OVERLAY_MARGIN,
            windowHeight - OVERLAY_HEIGHT - OVERLAY_MARGIN,
          ),
        )

  const handleSelectPoint = (
    point: VoiceAssessmentScorePoint,
    x: number,
    y: number,
  ) => {
    setSelectedPoint({
      point,
      anchorX: x - scrollXRef.current,
      anchorY: y,
      viewportX: 0,
      viewportY: 0,
      scrollX: scrollXRef.current,
    })
    chartViewportRef.current?.measureInWindow((pageX, pageY) => {
      setSelectedPoint({
        point,
        anchorX: pageX + x - scrollXRef.current,
        anchorY: pageY + y,
        viewportX: pageX,
        viewportY: pageY,
        scrollX: scrollXRef.current,
      })
    })
  }

  return (
    <View className="w-full flex-row">
      <Modal
        transparent
        visible={activeSelectedPoint != null}
        animationType="fade"
      >
        <View className="flex-1">
          <Pressable
            className="absolute inset-0"
            testID="score-chart-overlay-dismiss"
            onPress={() => setSelectedPoint(null)}
          />
          {activeSelectedPoint == null ? null : (
            <>
              {linePoints.map((point, index) => (
                <Pressable
                  key={`modal-hit-pt-${String(index)}`}
                  testID={`score-chart-modal-point-${String(index)}`}
                  accessibilityRole="button"
                  onPress={() =>
                    handleSelectPoint(point.point, point.x, point.y)
                  }
                  style={{
                    position: 'absolute',
                    left:
                      activeSelectedPoint.viewportX +
                      point.x -
                      activeSelectedPoint.scrollX -
                      POINT_HIT_SIZE / 2,
                    top:
                      activeSelectedPoint.viewportY +
                      point.y -
                      POINT_HIT_SIZE / 2,
                    width: POINT_HIT_SIZE,
                    height: POINT_HIT_SIZE,
                  }}
                />
              ))}
              <Pressable
                className="rounded-lg border border-border bg-background-secondary px-3 py-2 shadow-sm"
                testID="score-chart-overlay"
                onPress={(event) => event.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: overlayLeft,
                  top: overlayTop,
                  width: OVERLAY_WIDTH,
                  minHeight: OVERLAY_HEIGHT,
                }}
              >
                <Text
                  numberOfLines={1}
                  maxFontSizeMultiplier={1}
                  style={[headline2, { color: colors.textHeader }]}
                >
                  {activeSelectedPoint.point.displayScore}
                </Text>
                <Text
                  className="mt-1"
                  numberOfLines={1}
                  maxFontSizeMultiplier={1}
                  style={[body4, { color: colors.textMuted }]}
                >
                  {formatVoiceAssessmentChartDate(
                    activeSelectedPoint.point.created,
                    locale,
                  )}
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </Modal>
      <View>
        <View
          className="justify-between py-1"
          style={{ width: Y_AXIS_WIDTH, height: CHART_HEIGHT }}
        >
          {yTicks.map((tick) => (
            <Text
              key={tick}
              className="text-right pr-1"
              style={[body4, { color: colors.textMuted }]}
            >
              {tick}
            </Text>
          ))}
        </View>
        <View style={{ width: Y_AXIS_WIDTH, height: X_AXIS_LABEL_HEIGHT }} />
      </View>
      <View
        ref={chartViewportRef}
        className="min-w-0 flex-1"
        testID="score-chart-viewport"
        onLayout={onChartLayout}
      >
        {plotViewportWidth > 0 && plotContentWidth > 0 ? (
          <ScrollView
            ref={scrollViewRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            bounces={false}
            scrollEventThrottle={16}
            onScroll={(event) => {
              scrollXRef.current = event.nativeEvent.contentOffset.x
            }}
          >
            <View style={{ width: plotContentWidth }}>
              <View style={{ height: CHART_HEIGHT }}>
                <Svg width={plotContentWidth} height={CHART_HEIGHT}>
                  {yTicks.map((tick) => {
                    const y =
                      PADDING_Y +
                      ((Y_MAX - tick) / (Y_MAX - Y_MIN)) *
                        (CHART_HEIGHT - PADDING_Y * 2)
                    return (
                      <Line
                        key={`grid-${String(tick)}`}
                        x1={0}
                        y1={y}
                        x2={plotContentWidth}
                        y2={y}
                        stroke={GRID_COLOR}
                        strokeWidth={1}
                        strokeDasharray="4 4"
                      />
                    )
                  })}
                  {polylineSegments.map((polylinePoints, index) => (
                    <Polyline
                      key={`segment-${String(index)}`}
                      points={polylinePoints}
                      fill="none"
                      stroke={CHART_LINE}
                      strokeWidth={1}
                    />
                  ))}
                  {selectedLinePoint == null ? null : (
                    <Line
                      testID="score-chart-selection-line"
                      x1={selectedLinePoint.x}
                      y1={0}
                      x2={selectedLinePoint.x}
                      y2={CHART_HEIGHT}
                      stroke={colors.textMuted}
                      strokeOpacity={0.72}
                      strokeWidth={1.5}
                      strokeDasharray="3 5"
                    />
                  )}
                  {linePoints.map((point, index) => (
                    <Circle
                      key={`pt-${String(index)}`}
                      cx={point.x}
                      cy={point.y}
                      r={4}
                      fill={CHART_LINE}
                    />
                  ))}
                </Svg>
                {linePoints.map((point, index) => (
                  <Pressable
                    key={`hit-pt-${String(index)}`}
                    testID={`score-chart-point-${String(index)}`}
                    accessibilityRole="button"
                    onPress={() =>
                      handleSelectPoint(point.point, point.x, point.y)
                    }
                    style={{
                      position: 'absolute',
                      left: point.x - POINT_HIT_SIZE / 2,
                      top: point.y - POINT_HIT_SIZE / 2,
                      width: POINT_HIT_SIZE,
                      height: POINT_HIT_SIZE,
                    }}
                  />
                ))}
              </View>
              <View style={{ height: X_AXIS_LABEL_HEIGHT }}>
                {xLabelSlots.map((label) => {
                  return (
                    <View
                      key={`${label.line1}-${String(label.index)}`}
                      className="items-center px-0.5 pt-1.5"
                      testID={`score-chart-x-label-${String(label.index)}`}
                      style={{
                        position: 'absolute',
                        left: label.left,
                        width: label.width,
                      }}
                    >
                      <Text
                        className="text-center"
                        numberOfLines={1}
                        ellipsizeMode="clip"
                        maxFontSizeMultiplier={1}
                        style={[body4, { color: colors.textMuted }]}
                      >
                        {label.line1}
                      </Text>
                      {label.line2 != null && label.line2.length > 0 ? (
                        <Text
                          className="mt-0.5 text-center"
                          numberOfLines={1}
                          ellipsizeMode="clip"
                          maxFontSizeMultiplier={1}
                          style={[body4, { color: colors.textMuted }]}
                        >
                          {label.line2}
                        </Text>
                      ) : null}
                    </View>
                  )
                })}
              </View>
            </View>
          </ScrollView>
        ) : null}
      </View>
    </View>
  )
}
